// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a drawer content component for displaying detailed information
 * about a location, including its assets and audit history. It supports both regular
 * and transient locations with appropriate field visibility and editing capabilities.
 */
import { AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ArchiveControl } from '@/components/archive-control';
import { LocationName } from '@/components/location-name';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { CloseRounded, EditRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useSort } from '@/hooks/use-sort';
import { getLocation } from '@/location/api/get-location';
import { updateLocation } from '@/location/api/update-location';
import { EditLocationDialog } from '@/location/components/edit-location-dialog';
import { SiteAddressLabel } from '@/location/components/site-address-label';
import { SiteAuditList } from '@/location/components/site-audit-list';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Suspense, useState } from 'react';

/**
 * Columns to display in the asset table within the location drawer.
 * Limited to key information relevant in the location context.
 */
const ASSET_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'product',
  'owner',
  'activity',
];

interface Props {
  /**
   * The key of the location to display in the drawer.
   */
  locationKey: string;

  /**
   * A callback fired when the drawer requests to be closed.
   */
  onClose: () => void;
}

/**
 * A component that displays detailed information about a location in a drawer format.
 *
 * Features:
 * - Displays location name, company, and address
 * - Shows location status (archived/transient) with visual indicators
 * - Lists assets currently at the location in a sortable table
 * - Provides access to location audit history
 * - Supports location editing and archiving actions
 * - Handles both regular and transient locations with appropriate field visibility
 *
 * @example
 * ```tsx
 * <LocationDrawerContent
 *   locationKey="loc_123"
 *   onClose={() => setDrawerOpen(false)}
 * />
 * ```
 */
export function LocationDrawerContent(props: Props) {
  const { user } = useCurrentUser();
  const editDialog = useDisclosure();
  const queryClient = useQueryClient();

  // Configure asset table sorting and pagination
  const { sort, setSort } = useSort<AssetSortProperty>('serial');
  const [assetPage, setAssetPage] = useState(1);

  // Fetch location data
  const { data } = useSuspenseQuery({
    queryKey: ['location', props.locationKey],
    queryFn: () => getLocation(props.locationKey),
  });

  // Setup mutation for archiving/unarchiving locations
  const archiveLocationMutation = useMutation({
    mutationFn: (isArchived: boolean) => {
      return updateLocation(props.locationKey, data.location.type, {
        isArchived,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location'] });
    },
  });

  // Determine display values
  const companyName = data.location.customer?.name ?? user.organization.name;
  const archiveToggleTooltip = data.location.isArchived
    ? 'Unarchive location'
    : 'Archive location. Archiving does not change your location, but archived locations are hidden by default in the location list.';

  return (
    <Stack>
      <EditLocationDialog
        location={data.location}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
      />
      <Stack
        useFlexGap
        padding={2}
        spacing={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          marginRight="auto"
        >
          <LocationName location={data.location} variant="subtitle1" />
          {data.location.isArchived && <Chip size="small" label="Archived" />}
          {data.location.isTransient && (
            <Chip size="small" label="Transient" variant="outlined" />
          )}
          {data.location.isThirdPartyEnabled && (
            <Chip size="small" label="3PL" />
          )}
        </Stack>
        {!data.location.isTransient && (
          <Tooltip arrow title={archiveToggleTooltip}>
            <span>
              <ArchiveControl
                isArchived={data.location.isArchived}
                isDisabled={archiveLocationMutation.isPending}
                onChange={archiveLocationMutation.mutate}
              />
            </span>
          </Tooltip>
        )}
        <IconButton size="small" onClick={editDialog.open}>
          <EditRounded fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={props.onClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Stack>
      <Divider />
      <Stack direction="row" paddingX={2} marginTop={2} spacing={4}>
        <Box display="grid" gridTemplateColumns="auto 1fr" gap={1}>
          {!data.location.isTransient && (
            <>
              <Typography variant="subtitle2">Company</Typography>
              <Typography variant="body2" color="text.secondary">
                {companyName}
              </Typography>
            </>
          )}
          <Typography variant="subtitle2">Address</Typography>
          <SiteAddressLabel site={data.location} variant="body2" />
          {!data.location.isTransient && (
            <>
              <Typography variant="subtitle2">Include in inventory</Typography>
              <Typography variant="body2" color="text.secondary">
                {data.location.isInventory ? 'Yes' : 'No'}
              </Typography>
            </>
          )}
        </Box>
      </Stack>
      <Stack paddingX={2} marginTop={2} spacing={1}>
        <Typography variant="subtitle2">Assets</Typography>
        <Suspense
          fallback={<AssetTableSkeleton columns={ASSET_TABLE_COLUMNS.length} />}
        >
          <AssetTable
            sort={sort}
            columns={ASSET_TABLE_COLUMNS}
            activityLayout="stacked"
            filter={{
              locationIds: [data.location.id],
              pageSize: 50,
              pageNumber: assetPage,
            }}
            onSort={setSort}
            onPageChange={setAssetPage}
            emptyState={
              <Typography variant="body2" color="text.secondary">
                There are no assets at this location
              </Typography>
            }
          />
        </Suspense>
      </Stack>
      <Stack paddingX={2} marginTop={2} spacing={1}>
        <Typography variant="subtitle2">Audit log</Typography>
        <SupportErrorBoundary message="An error occurred while loading the audit log.">
          <Suspense>
            <SiteAuditList siteKey={props.locationKey} />
          </Suspense>
        </SupportErrorBoundary>
      </Stack>
    </Stack>
  );
}
